import { CustomerEndPoints } from "../constants/endpoints";
import { api } from "./api";
import axios from "axios";
import { store } from "../redux/store";
import { setCustomerData } from "../redux/features/slices/appSlice";

type FetchCustomerProps = {
  page: number | undefined;
  limit: number | undefined;
  search?: string;
  download?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: string;
};

type FetchCustomerByEmailProps = {
  emailId: string;
};

/**
 * Uploads a file to S3 using a presigned URL.
 * @param presignedUrl - The presigned URL from S3.
 * @param fileData - The file content (blob or base64).
 * @param fileType - The MIME type of the file.
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise.
 */
export const uploadFileToS3Api = async (
  presignedUrl: string,
  fileData: Blob | ArrayBuffer,
  fileType: string
): Promise<boolean> => {
  try {
    const response = await api.put(presignedUrl, fileData, {
      headers: {
        "Content-Type": fileType,
      },
    });

    if (response.status === 200) {
      console.log("File uploaded successfully to S3");
      return true;
    } else {
      console.error("Failed to upload file", response.data);
      return false;
    }
  } catch (error) {
    console.error("Error uploading file:", error);
    return false;
  }
};

export const fetchCustomers = async (props: FetchCustomerProps) => {
  try {
    return await api.get(CustomerEndPoints.getCustomers, {
      params: {
        page: props.page,
        limit: props.limit,
        search: props.search,
        startDate: props.startDate,
        endDate: props.endDate,
        download: props.download,
        sortOrder: props.sortOrder,
      },
    });
  } catch (error) {
    throw new Error("Failed to fetch customers");
  }
};

export const fetchCustomersByEmail = async (
  props: FetchCustomerByEmailProps
) => {
  const customerData = store.getState().app.customerData;
  if (!customerData) {
    try {
      const endpointsUrl = `${CustomerEndPoints.getCustomerByEmail}/${props.emailId}`;
      const response = await api.get(endpointsUrl);
      store.dispatch(setCustomerData(response.data.data));
      return response.data.data;
    } catch (error) {
      throw new Error("Failed to fetch customers");
    }
  }
  return customerData;
};

export const getCustomerById = async (
  userID: string | undefined
): Promise<any> => {
  try {
    const response = await api.get(
      `${CustomerEndPoints.getCustomer}/${userID}`
    );
    console.log("Customer fetched successfully:", response.data);
    return response;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

export const deleteCustomer = async (
  userID: string,
  emailID: string
): Promise<any> => {
  try {
    const response = await api.delete(CustomerEndPoints.deleteCustomer, {
      data: { id: userID, email: emailID },
    });
    console.log("Customer deleted successfully:", response.data);
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

export const editCustomer = async (
  userID: string,
  updatePayload: any
): Promise<void> => {
  try {
    const response = await api.put(
      `${CustomerEndPoints.editCustomer}/${userID}`,
      updatePayload
    );
    console.log("Customer updated successfully:", response.data);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const createNewCustomer = async (
  payload: any,
  finalDocumentData: any[]
): Promise<void> => {
  try {
    const response = await api.post(CustomerEndPoints.createNewCustomer, {
      ...payload,
      documents: finalDocumentData,
    });
    console.log("Customer created successfully:", response.data);
    return response.data.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const getPresignedUrls = async (
  updatedPayload: any[],
  bucketKey = ""
): Promise<any[] | undefined> => {
  try {
    const response = await api.post(CustomerEndPoints.getPresignedUrl, {
      documents: updatedPayload,
      bucketKey,
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error);
    return undefined;
  }
};

const handleApiError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    console.error("Axios error:", error.response?.data || error.message);
  } else {
    console.error("Unexpected error:", error);
  }
  throw error;
};
